import { message, Progress, Tooltip, Typography, Upload } from 'antd';
import { ReloadOutlined, InfoCircleFilled } from '@ant-design/icons';
import { useAppDispatch } from 'app/hooks';
import FilePDF from 'assets/icons/file-types/pdf-icon.svg';
import axios, { CancelTokenSource } from 'axios';
import { FileDeleteIcon, FileDownloadIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import { RevxLoader } from 'components/RevxLoader';
import { toast } from 'components/RevxMessage';
import { Text } from 'components/RevxTypography';
import { JobConfig } from 'domains/jobconfigDomain';
import { SKU } from 'domains/skuDomain';
import { JobItem } from 'domains/workspaceDomain';
import { sortBy } from 'lodash';
import dayjs from 'dayjs';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import jobconfigService from 'services/jobconfigService';
import jobService from 'services/jobService';
import uploadFileService from 'services/uploadService';
import {
  JOB_FILE_MAX_NUM,
  UPLOAD_ACCEPT,
  UPLOAD_FILE_ADDRESS_PREFIX,
  UPLOAD_FILE_NAME_MAX_LENGTH,
  UPLOAD_FILE_THUMBNAIL_ADDRESS_PREFIX,
} from 'utils/constants';
import { getFileExtension, getFileIcon, isImage, isPDF } from 'utils/file';
import { sleep } from 'utils/misc';
import { flushHighResAndHandle } from 'utils/canvaHighRes';
import { useUser } from 'hooks';
import './SkuUploadFile.less';

export const SkuUploadFile: React.FC<{
  configItem: JobConfig;
  sku?: SKU;
  onUploadingStatusChange?: (uploading: boolean) => void;
  onUpdate?: (configItem: JobConfig) => void;
  disabled?: boolean;
  canDelete?: boolean;
  jobItem?: JobItem;
}> = ({
  configItem,
  sku,
  onUpdate,
  onUploadingStatusChange,
  disabled = false,
  canDelete = true,
  jobItem,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useUser();
  const cancelSources = useRef<CancelTokenSource[]>([]);
  const [isUploading, setIsUploading] = useState(disabled);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [flushingHighResId, setFlushingHighResId] = useState<string | null>(
    null
  );
  const onCancelUpload = () => {
    cancelSources.current.forEach((source) => {
      source.cancel();
    });
  };

  const flushHighRes = async (fileId: string) => {
    if (flushingHighResId) {
      return;
    }
    setFlushingHighResId(fileId);
    try {
      await flushHighResAndHandle(() =>
        jobService.flushHighResByJobConfigId(configItem.id)
      );
    } finally {
      setFlushingHighResId(null);
    }
  };
  const skuFiles = useMemo(() => {
    let files = configItem.artFiles.filter((item) => {
      return (item.skuId || 0) === (sku?.id || 0);
    });

    return sortBy(files, (item) => -dayjs(item.createAt));
  }, [configItem.artFiles, sku?.id]);

  const deleteFile = async (fileId: string) => {
    dispatch.jobConfig.setState({
      revxSpinloading: true,
    });
    try {
      const newConfigItem = await jobconfigService.deleteFile(
        configItem.id,
        fileId
      );

      await dispatch.jobConfig.getShippingCalendar({});

      dispatch.jobConfig.setState({
        configItem: newConfigItem,
      });

      if (onUpdate) {
        onUpdate(newConfigItem);
      }
    } catch {}
    dispatch.jobConfig.setState({
      revxSpinloading: false,
    });
  };

  return (
    <div className="sku-upload-file">
      <div hidden={configItem?.skipArtFile || disabled}>
        <Upload.Dragger
          className="sku-upload-file-item"
          disabled={isUploading}
          showUploadList={false}
          accept={UPLOAD_ACCEPT}
          onDrop={(e) => {
            if (e?.dataTransfer?.files?.length > 1) {
              toast.error(t('common.addOneFile'));
            }
          }}
          beforeUpload={async (file, fileList) => {
            if (skuFiles.length + 1 > JOB_FILE_MAX_NUM) {
              message.error(t('common.fileMaxLengthMessage'));
              return Promise.reject();
            }

            if (file.size <= 0) {
              message.error(t('common.fileEmptyMessage'));
              return Promise.reject();
            }

            if (file.name.length > UPLOAD_FILE_NAME_MAX_LENGTH) {
              message.error(
                t('common.fileMaxNameMessage', {
                  length: UPLOAD_FILE_NAME_MAX_LENGTH,
                })
              );

              return Promise.reject();
            }

            setIsUploading(true);
            if (onUploadingStatusChange) {
              onUploadingStatusChange(true);
            }

            setUploadProgress(0);
            const source = axios.CancelToken.source();
            cancelSources.current.push(source);
            const ret = await uploadFileService.uploadFile({
              file,
              cancelTokenObject: {
                token: source.token,
              },
              onUploadProgress: (progress) => {
                const ratio = progress.loaded / file.size;
                setUploadProgress(Math.ceil(ratio * 100));
              },
            });

            if (isPDF(file.name)) {
              await sleep(3000);
            }

            if (ret) {
              const item = await jobconfigService.addFileToJobConfig(
                ret.fileName,
                new URL(ret.path).pathname.replace(/^\//, ''),
                configItem.id,
                sku?.id
              );

              await dispatch.jobConfig.getShippingCalendar({});

              dispatch.jobConfig.setState({
                configItem: item,
              });

              if (onUpdate) {
                onUpdate(item);
              }
            }

            setIsUploading(false);

            if (onUploadingStatusChange) {
              onUploadingStatusChange(false);
            }
            setUploadProgress(0);

            return Promise.reject();
          }}
        >
          <div className="sku-upload-file-button">
            <div className="bold">DRAG & DROP</div>
            <div>OR</div>
            <div>CLICK TO UPLOAD</div>
          </div>
        </Upload.Dragger>
      </div>

      {Boolean(isUploading && !disabled) && (
        <div className="sku-upload-file-uploading">
          <div className="loading-icon">
            <RevxLoader loading size="large" fixed={false} />
          </div>
          <Progress
            percent={uploadProgress}
            status={uploadProgress >= 100 ? 'success' : 'active'}
            showInfo={false}
          />
          <RevxButton
            brandOrange
            type="primary"
            size="small"
            onClick={onCancelUpload}
          >
            Cancel
          </RevxButton>
        </div>
      )}

      {skuFiles.map((skuFile) => {
        const extension = getFileExtension(skuFile.fileName);
        const fileIsImage =
          isImage(skuFile.fileName) || isImage(skuFile.fileUrl);
        const fileIsPDF = isPDF(skuFile.fileName) || isPDF(skuFile.fileUrl);
        // Everyone (Buyer / Supplier / Sourcer) sees the print-ready hint:
        // while a high-resolution file is still being processed (inline
        // warning) vs. already print-ready (hover tooltip). Only the job
        // creator additionally gets the refresh button.
        const hasHighResFlag = skuFile.needHighResolution !== undefined;
        const isHighResProcessing =
          hasHighResFlag && Boolean(skuFile.needHighResolution);
        const isPrintReady = hasHighResFlag && !skuFile.needHighResolution;
        const isJobCreator =
          jobItem?.customerId !== undefined &&
          jobItem.customerId === user?.userId;
        const showRefreshHighRes = isHighResProcessing && isJobCreator;
        const fileItem = (
          <div className="sku-upload-file-item" key={skuFile.id}>
            {showRefreshHighRes && (
              <Tooltip title="Refresh high-resolution PDF">
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    flushHighRes(skuFile.id);
                  }}
                  style={{
                    position: 'absolute',
                    top: 6,
                    left: 6,
                    zIndex: 1,
                    cursor:
                      flushingHighResId === skuFile.id
                        ? 'not-allowed'
                        : 'pointer',
                  }}
                >
                  <ReloadOutlined
                    spin={flushingHighResId === skuFile.id}
                    style={{ fontSize: 16, color: '#009088' }}
                  />
                </span>
              </Tooltip>
            )}
            {fileIsImage && (
              <span className="preview-img">
                <img
                  src={`${UPLOAD_FILE_ADDRESS_PREFIX}${skuFile.fileUrl}`}
                  alt="none"
                />
              </span>
            )}
            {fileIsPDF && (
              <span className="preview-img">
                <img
                  style={{ fontSize: 60 }}
                  onError={function (e) {
                    const img = e.target as HTMLImageElement;
                    const src = img.currentSrc;
                    img.src = FilePDF;
                    if (isPDF(src)) {
                      setTimeout(function () {
                        img.src = src;
                      }, 3000);
                    }
                  }}
                  src={`${UPLOAD_FILE_THUMBNAIL_ADDRESS_PREFIX}${skuFile.fileUrl}`}
                  alt="none"
                />
              </span>
            )}

            {!fileIsImage && !fileIsPDF && (
              <span className="preview-icon">{getFileIcon(extension)}</span>
            )}
            <Text
              ellipsis={{
                tooltip: skuFile.fileName,
              }}
              className="sku-upload-file-item-name"
            >
              {skuFile.fileName}
            </Text>
            <span
              onClick={() => {
                axios
                  .get(`${UPLOAD_FILE_ADDRESS_PREFIX}${skuFile.fileUrl}` || '')
                  .then((res) => {
                    if (res.status === 200) {
                      window.open(
                        `${UPLOAD_FILE_ADDRESS_PREFIX}${skuFile.fileUrl}`
                      );
                    } else {
                      if (onUpdate) {
                        onUpdate(configItem);
                      }
                    }
                  })
                  .catch((error) => {
                    if ((error + '').indexOf('404') >= 0) {
                      if (onUpdate) {
                        onUpdate(configItem);
                      }
                    } else {
                      window.open(
                        `${UPLOAD_FILE_ADDRESS_PREFIX}${skuFile.fileUrl}`
                      );
                    }
                  });
              }}
            >
              <FileDownloadIcon className="file-action-icon" />
            </span>
            {canDelete && !configItem?.skipArtFile && !disabled && (
              <span onClick={() => deleteFile(skuFile.id)}>
                <FileDeleteIcon
                  className="file-action-icon"
                  style={{ top: 30 }}
                />
              </span>
            )}
          </div>
        );

        if (isHighResProcessing) {
          return (
            <div
              key={skuFile.id}
              style={{
                display: 'inline-flex',
                flexDirection: 'column',
                width: 100,
              }}
            >
              {fileItem}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 4,
                  marginTop: 6,
                }}
              >
                <InfoCircleFilled
                  style={{ color: '#faad14', marginTop: 2, flexShrink: 0 }}
                />
                <Typography.Text
                  type="warning"
                  style={{ fontSize: 12, lineHeight: 1.3 }}
                >
                  Print-ready file processing
                </Typography.Text>
              </div>
            </div>
          );
        }

        if (isPrintReady) {
          return (
            <Tooltip key={skuFile.id} title="Print-ready">
              {fileItem}
            </Tooltip>
          );
        }

        return fileItem;
      })}
    </div>
  );
};
